
import {defineComponent} from "vue";
import PhotoCaptureService from "@/services/photo-capture.service";
import ToastService from "@/services/toast.service";

/** Longest edge of a captured photo, matching photo-capture.service. */
const MAX_EDGE = 1000;

/** JPEG quality of a captured photo, matching photo-capture.service. */
const JPEG_QUALITY = 0.7;

/**
 * In-app camera with its own lamp toggle.
 *
 * Built on getUserMedia rather than the system camera so the monteur never leaves
 * the page, which is what the ticket's design asks for. The lamp is the `torch`
 * track constraint, which works on Chromium and, since iOS 17.5, on WebKit too.
 * See docs/photos.md.
 */
export default defineComponent({
    name: "InAppCameraModal",
    emits: ["captured"],
    data() {
        return {
            isOpen: false,
            isCapturing: false,
            hasTorch: false,
            torchOn: false,
            hasMultipleCameras: false,
            facingMode: "environment" as "environment" | "user",
            stream: null as MediaStream | null,
            // Bumped by stopStream(); a startStream() still awaiting the camera
            // compares against it and releases a stream that arrives too late.
            streamRequest: 0,
        };
    },
    beforeUnmount() {
        // Without this the camera keeps running when the monteur navigates away.
        this.stopStream();
    },
    mounted() {
        document.addEventListener("visibilitychange", this.releaseWhenHidden);
        window.addEventListener("pagehide", this.stopStream);
    },
    unmounted() {
        document.removeEventListener("visibilitychange", this.releaseWhenHidden);
        window.removeEventListener("pagehide", this.stopStream);
    },
    methods: {
        /**
         * Opens the camera. When the camera cannot be opened a toast says so,
         * and the overlay stays open so the monteur can still pick a photo
         * from the gallery.
         */
        async open(): Promise<void> {
            this.isOpen = true;
            this.isCapturing = false;

            try {
                await this.startStream();
            } catch (error) {
                PhotoCaptureService.reportCaptureFailure(error);
                await ToastService.presentToast(
                    "De camera kan niet worden geopend. Kies een foto uit je galerij.",
                    4000,
                    "danger"
                );
            }
        },

        /**
         * Starts a stream for the current facing mode and reads its capabilities.
         *
         * Everything awaited here can be overtaken by close(), a tab switch or a
         * newer start; those all bump the request token through stopStream(), and
         * a stale request releases its stream instead of adopting it. The stream
         * stays local until initialisation has fully succeeded, so a failure
         * halfway never leaves the camera held without the component knowing.
         */
        async startStream(): Promise<void> {
            this.stopStream();
            const request = this.streamRequest;

            // The <video> lives behind `v-if="isOpen"`, so wait for it to exist
            // rather than relying on microtask ordering to have rendered it.
            await this.$nextTick();

            let stream: MediaStream | null = null;

            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: {facingMode: {ideal: this.facingMode}},
                    audio: false,
                });

                const [track] = stream.getVideoTracks();
                const capabilities =
                    typeof track?.getCapabilities === "function" ? track.getCapabilities() : {};

                const devices = await navigator.mediaDevices.enumerateDevices();

                if (request !== this.streamRequest) {
                    stream.getVideoTracks().forEach((lateTrack) => lateTrack.stop());
                    return;
                }

                this.stream = stream;

                // A lamp only exists on the rear camera of most devices. Chromium
                // reports the capability as a boolean, the spec says sequence<boolean>;
                // anything else (absent key, false, [false], []) means no lamp.
                const torch = (capabilities as {torch?: boolean | boolean[]}).torch;
                this.hasTorch =
                    torch === true || (Array.isArray(torch) && torch.includes(true));
                this.torchOn = false;

                this.hasMultipleCameras =
                    devices.filter((device) => device.kind === "videoinput").length > 1;

                const preview = this.$refs.preview as HTMLVideoElement | undefined;

                if (preview) {
                    preview.srcObject = stream;
                }
            } catch (error) {
                stream?.getVideoTracks().forEach((track) => track.stop());
                throw error;
            }
        },

        /**
         * Switches the lamp on or off.
         */
        async toggleTorch(): Promise<void> {
            const track = this.stream?.getVideoTracks()[0];

            if (!track) {
                return;
            }

            const next = !this.torchOn;

            try {
                await track.applyConstraints({advanced: [{torch: next} as any]});
                this.torchOn = next;
            } catch (error) {
                this.hasTorch = false;
                PhotoCaptureService.reportCaptureFailure(error);
            }
        },

        /**
         * Swaps between the front and rear camera. When the other camera will
         * not open, falls back to the one that was live, so the preview is not
         * left dead.
         */
        async flipCamera(): Promise<void> {
            const previous = this.facingMode;
            this.facingMode = previous === "environment" ? "user" : "environment";

            try {
                await this.startStream();
            } catch (error) {
                PhotoCaptureService.reportCaptureFailure(error);
                this.facingMode = previous;

                try {
                    await this.startStream();
                } catch {
                    // Already reported above; the gallery button still works.
                }
            }
        },

        /**
         * Grabs the current frame, scaled down and encoded as JPEG.
         *
         * A video frame carries no EXIF orientation, so unlike a file from the
         * gallery this needs no rotation handling.
         */
        async capture(): Promise<void> {
            const preview = this.$refs.preview as HTMLVideoElement | undefined;

            if (this.isCapturing) {
                return;
            }

            if (!preview || !preview.videoWidth) {
                await ToastService.presentToast(
                    "De camera is nog niet klaar. Probeer het opnieuw.",
                    4000,
                    "danger"
                );
                return;
            }

            this.isCapturing = true;

            try {
                const scale = Math.min(
                    1,
                    MAX_EDGE / Math.max(preview.videoWidth, preview.videoHeight)
                );

                const canvas = document.createElement("canvas");
                canvas.width = Math.round(preview.videoWidth * scale);
                canvas.height = Math.round(preview.videoHeight * scale);

                const context = canvas.getContext("2d");

                if (!context) {
                    throw new Error("2d canvas context unavailable");
                }

                context.drawImage(preview, 0, 0, canvas.width, canvas.height);

                const blob = await new Promise<Blob | null>((resolve) => {
                    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
                });

                if (!blob) {
                    throw new Error("canvas produced no blob");
                }

                this.$emit("captured", new File([blob], "photo.jpg", {type: "image/jpeg"}));
                await this.close();
            } catch (error) {
                PhotoCaptureService.reportCaptureFailure(error);
                await ToastService.presentToast(
                    "De foto kan niet worden gemaakt. Probeer het opnieuw.",
                    4000,
                    "danger"
                );
            } finally {
                this.isCapturing = false;
            }
        },

        /**
         * Hands a gallery photo up, re-encoded like any other capture.
         * @param event
         */
        async onGallerySelected(event: Event): Promise<void> {
            const input = event.target as HTMLInputElement;
            const photo = input.files?.length ? input.files[0] : null;

            // Reset so picking the same photo twice still fires a change event.
            input.value = "";

            if (!photo) {
                return;
            }

            const prepared = await PhotoCaptureService.prepareForUpload(photo);

            this.$emit("captured", prepared.file);
            await this.close();
        },

        /**
         * Closes the overlay and releases the camera.
         */
        async close(): Promise<void> {
            this.stopStream();
            this.isOpen = false;
        },

        /**
         * Releases the camera when the tab goes to the background, so returning to
         * it does not find the device already held. The overlay stays open: going
         * hidden also happens when the gallery picker opens, and unmounting the
         * file input mid-pick would break it. The stream restarts once the page
         * is visible again.
         */
        async releaseWhenHidden(): Promise<void> {
            if (document.visibilityState === "hidden") {
                this.stopStream();
                return;
            }

            if (!this.isOpen || this.stream) {
                return;
            }

            try {
                await this.startStream();
            } catch (error) {
                PhotoCaptureService.reportCaptureFailure(error);
            }
        },

        /**
         * Switches the lamp off and stops every track. Also invalidates any
         * startStream() still waiting on the camera, so a stream that arrives
         * after this call is released instead of adopted.
         */
        stopStream(): void {
            this.streamRequest++;

            if (!this.stream) {
                return;
            }

            this.stream.getVideoTracks().forEach((track) => {
                if (this.torchOn) {
                    // Best effort: the track is about to stop anyway.
                    track.applyConstraints({advanced: [{torch: false} as any]}).catch(() => undefined);
                }

                track.stop();
            });

            this.stream = null;
            this.torchOn = false;
        },
    },
});
